import { useState } from "react";
import { Search, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import type { NewsletterSubscriber } from "@endeks/shared";
import { useNewsletterSubscribers, useNewsletterStats, useDeleteSubscriber } from "./queries";
import { showDeleteConfirm } from "../../components/Common/DeleteConfirm";
import { QueryError } from "../../components/Common/QueryError";
import { Pagination } from "../../components/Common/Pagination";
import { errorMessage } from "@/lib/error-message";
import { useDebouncedValue } from "@/lib/use-debounced-value";
import { usePagination } from "@/lib/use-pagination";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

// NewsletterSubscriber from shared is missing createdAt — the API always returns it.
type NewsletterRow = NewsletterSubscriber & { id: string; createdAt: string };

export default function NewsletterList() {
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const {
    data: rawSubscribers,
    isLoading,
    isError,
    error,
    refetch,
  } = useNewsletterSubscribers(debouncedSearch || undefined);
  const subscribers: NewsletterRow[] = (rawSubscribers ?? []) as NewsletterRow[];
  const { pageItems, page, totalPages, totalItems, setPage } = usePagination(
    subscribers,
    20,
    debouncedSearch,
  );

  const { data: stats } = useNewsletterStats();
  const deleteMutation = useDeleteSubscriber();

  function handleDelete(id: string) {
    showDeleteConfirm({
      onConfirm: () => {
        deleteMutation.mutate(id, {
          onSuccess: () => toast.success("Abone silindi"),
          onError: (e: unknown) => toast.error(errorMessage(e, "Silinemedi")),
        });
      },
    });
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-muted-foreground" />
            <h1 className="text-xl font-semibold">Bülten Aboneleri</h1>
          </div>
          {stats && (
            <span className="text-sm text-muted-foreground">
              {stats.total} toplam,{" "}
              <span className="text-green-700 font-medium">{stats.active} aktif</span>
            </span>
          )}
        </div>
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : isError && !rawSubscribers ? (
        <QueryError error={error} onRetry={() => void refetch()} />
      ) : subscribers.length === 0 ? (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm">
          {debouncedSearch ? "Arama sonucu bulunamadı." : "Henüz abone yok."}
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>E-posta</TableHead>
                <TableHead className="w-28">Durum</TableHead>
                <TableHead className="w-32">Tarih</TableHead>
                <TableHead className="w-20 text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pageItems.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell className="font-medium">{sub.email}</TableCell>
                  <TableCell>
                    {sub.active ? (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                        Aktif
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-600 border-red-200">
                        Pasif
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {new Date(sub.createdAt).toLocaleDateString("tr-TR")}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(sub.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            page={page}
            totalPages={totalPages}
            totalItems={totalItems}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
